import Cookies from "js-cookie";
import _ from "lodash/fp";
import { unparse } from "papaparse";

const asyncPipe =
  (...functions) =>
  (input) =>
    functions.reduce((chain, func) => chain.then(func), Promise.resolve(input));

// The manageMetafileAssets branch here only decides whether the save request
// fires at all; it doesn't check *what* changed. The complementary "only
// metafileAssets is actually dirty" check lives in Workspace/index.js's
// hasMetafileOnlySaveOverride - keep both in sync if either changes, and
// note the server independently enforces this via checkPermissions.
const canSave =
  (type) =>
  ({ actions, activeTasks }) => {
    const inWorkflow = ["item", "passage"].some((key) => activeTasks[key]);
    return (
      (inWorkflow &&
        activeTasks[type] &&
        !getTaskPermissions(activeTasks[type]).includes("readonly")) ||
      (!inWorkflow &&
        (actions.includes("edit") || actions.includes("manageMetafileAssets")))
    );
  };

const convertSavedTasks = (savedTasks) => (updatedTasksResponse) => {
  const convertedTasks = savedTasks.map((task) => {
    if (task?.taskID === updatedTasksResponse[0].id) {
      return {
        ...task,
        ..._.pickBy(_.identity)(updatedTasksResponse[0]),
      };
    } else {
      return task;
    }
  });

  return convertedTasks;
};

const convertTasks = (values) => (dirtyFields) => {
  const { item = {}, passage = {} } = values;
  const { tasks: itemTasks = [] } = item;
  const { tasks: passageTasks = [] } = passage;

  const dirtyItemTaskFields = dirtyFields?.item?.tasks || [];
  const dirtyPassageTaskFields = dirtyFields?.passage?.tasks || [];

  const itemTasksToUpdate = dirtyItemTaskFields
    .map((field, i) => ({
      ...getDirtyRHFValues(field)(itemTasks[i]),
    }))
    .filter(Boolean);

  const passageTasksToUpdate = dirtyPassageTaskFields
    .map((field, i) => ({
      ...getDirtyRHFValues(field)(passageTasks[i]),
    }))
    .filter(Boolean);

  return { itemTasksToUpdate, passageTasksToUpdate };
};

const displayFileSize = (size) => {
  if (!Number.isInteger(size)) return "--";
  const breakpoint = [
    { size: 1024 * 1024, unit: "MB" },
    { size: 1024, unit: "KB" },
    { size: 1, unit: "B" },
  ].find((s) => size > s.size);
  const decimal = (size / breakpoint?.size).toLocaleString("en", {
    maximumFractionDigits: 1,
  });
  return `${decimal} ${breakpoint?.unit}`;
};

const downloadCsv = (data) => (filename) => {
  if (!data?.data?.length) return;

  const csvContent = unparse(data, { quotes: true });
  const blob = new Blob([`\ufeff${csvContent}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = window.URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.setAttribute("href", url);
  a.setAttribute("download", `${filename}.csv`);
  a.click();
  a.remove();
};

const dueDateTransform = (dueDate) => {
  const newDueDate = new Date(dueDate);
  const offset = newDueDate.getTimezoneOffset();
  newDueDate.setTime(newDueDate.getTime() + offset * 60 * 1000);
  return newDueDate;
};

const escapeUTF8Characters = (str) => {
  let result = "";
  for (let i = 0; i < str.length; i++) {
    const charCode = str.charCodeAt(i);
    if (charCode > 127) {
      // Character is a UTF-8 character, escape it
      result += str[i].replace(/[\u00A0-\u9999<>&]/g, function (c) {
        return `&#${c.charCodeAt(0)};`;
      });
    } else {
      // Character is not a UTF-8 character, add it to the result as-is
      result += str[i];
    }
  }
  return result;
};

const filterOutSelectedPrograms = (programOptions) => (checkedRows) =>
  programOptions.filter((program) => {
    const selectedRowsProgramIds = checkedRows.map((obj) => obj?.program);
    return !selectedRowsProgramIds.includes(program?.value);
  });

const filterUsers =
  (users = []) =>
  ({ activeOnly = true, programs = [], roles = [] }) => {
    if (!users.length) return [];
    return users
      .filter(
        (user) =>
          (!activeOnly || !!user.azureId) &&
          user?.programAccess.find(
            ({ program, reviewTypes }) =>
              (!programs.length || programs.includes(program)) &&
              (!roles.length ||
                roles.every((role) => reviewTypes.includes(role))),
          ),
      )
      .sort(sortByName);
  };

const getAssetInfo = (objectID) => (asset) => {
  console.debug(
    "Called getAssetInfo for object %s and asset %o",
    objectID,
    asset,
  );
  switch (asset.__typename) {
    case "MediaAsset":
    case "GraphicAsset":
      return getNonImageAssetInfo(objectID)(asset);
    case "ImageAsset":
      return getImageInfo(objectID)(asset);
    case "MetafileAsset":
      return getNonImageAssetInfo(objectID)(asset);
    case "ProgramMetafileAsset":
      return getNonImageAssetInfo(objectID)(asset);
    default:
  }
};

const getPriorCandidates = ({ completedTasks, taskId, taskCompletedAt }) =>
  (completedTasks || [])
    .filter(
      (candidate) =>
        candidate.id !== taskId &&
        candidate.snapshotVersionId &&
        candidate.completedDateTime &&
        new Date(candidate.completedDateTime).getTime() < taskCompletedAt,
    )
    .sort(
      (a, b) =>
        new Date(b.completedDateTime).getTime() -
        new Date(a.completedDateTime).getTime(),
    );

const sortInactiveTasksByCompletedDate = (tasks) =>
  [...(tasks || [])].sort((a, b) => {
    if (!a.completedDateTime && !b.completedDateTime) return 0;
    if (!a.completedDateTime) return -1;
    if (!b.completedDateTime) return 1;
    return (
      new Date(b.completedDateTime).getTime() -
      new Date(a.completedDateTime).getTime()
    );
  });

// Candidates are sorted newest-first, so only the contiguous run sharing the
// most-recent prior's developmentState counts as concurrent — a same-state task
// from an earlier cycle (separated by a different state) is not ambiguous.
const isAmbiguousPredecessor = (priorCandidates) => {
  const [prior] = priorCandidates;
  if (!prior) return false;

  if (!prior.developmentState) return false;

  const firstDifferentIndex = priorCandidates.findIndex(
    (candidate) => candidate.developmentState !== prior.developmentState,
  );
  const concurrentPriorCount =
    firstDifferentIndex === -1 ? priorCandidates.length : firstDifferentIndex;

  return concurrentPriorCount > 1;
};

const getCompareVersions = ({ task, completedTasks, latestVersionId }) => {
  const current =
    task?.snapshotVersionId && latestVersionId
      ? { left: task.snapshotVersionId, right: latestVersionId }
      : null;

  const priorCandidates = getPriorCandidates({
    completedTasks,
    taskId: task?.id,
    taskCompletedAt: new Date(task?.completedDateTime).getTime(),
  });
  const prior = priorCandidates[0];

  const lastTask =
    prior && task?.snapshotVersionId && !isAmbiguousPredecessor(priorCandidates)
      ? { left: prior.snapshotVersionId, right: task.snapshotVersionId }
      : null;

  return { current, lastTask };
};

const getDirtyRHFValues = (dirtyFields) => (values) => {
  return Object.keys(dirtyFields).reduce(
    (prev, key) => {
      if (dirtyFields[key]) {
        const newValues =
          typeof dirtyFields[key] === "object"
            ? getDirtyRHFValues(dirtyFields[key])(values[key])
            : values[key];
        prev[key] = newValues;
      }
      return prev;
    },
    { taskIDs: [values.taskID] },
  );
};

const getFileType = (asset) => {
  return asset?.name.split(".").pop().toUpperCase();
};

const fetchWithAuth = (src) => {
  const token = Cookies.get("TOKEN");
  return fetch(src, {
    headers: token ? { Authorization: token } : {},
  });
};

const loadImageDimensions = (blobUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error("Error loading image"));
    };
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      resolve({ height: img.naturalHeight, width: img.naturalWidth });
    };
    img.src = blobUrl;
  });

const fetchImageBlob = async (src) => {
  const response = await fetchWithAuth(src);
  if (!response.ok) throw new Error(`Failed to load image: ${response.status}`);
  const contentType = response.headers.get("content-type") ?? "";
  if (
    !contentType.startsWith("image/") &&
    contentType !== "application/octet-stream"
  ) {
    throw new Error(`Unexpected content-type for ${src}: ${contentType}`);
  }
  return response.blob();
};

const getImageFileData = async (src) => {
  if (!src) return {};
  const blob = await fetchImageBlob(src);
  const blobUrl = URL.createObjectURL(blob);
  const { height, width } = await loadImageDimensions(blobUrl);
  return { fileSize: blob.size, height, width };
};

// Extracts the (decoded) filename from an asset src/path. Image assets live at
// /assets/{objectID}/{encodeURIComponent(name)}, so the trailing path segment is
// the stable identifier across passages — unlike the full path, which embeds the
// objectID and therefore differs between an original passage and its clone.
const getAssetFileName = (src = "") => {
  try {
    return decodeURIComponent((src.split("/").pop() || "").split("?")[0]);
  } catch {
    return (src.split("/").pop() || "").split("?")[0];
  }
};

const getImageInfo = (objectID) => async (image) => {
  const assetsSrc = `/assets/${objectID}/${encodeURIComponent(image.name)}`;
  const uploadsSrc = `/asset_uploads/${encodeURIComponent(image.name)}`;

  const resolveImageSrc = async () => {
    try {
      const data = await getImageFileData(assetsSrc);
      return { ...data, src: assetsSrc };
    } catch (err) {
      console.warn(
        `getImageInfo: primary URL failed for ${assetsSrc}, trying upload path`,
        err.message,
      );
      const data = await getImageFileData(uploadsSrc);
      return { ...data, src: uploadsSrc };
    }
  };

  return Promise.allSettled([
    resolveImageSrc(),
    getNonImageAssetInfo(objectID)(image.printableAsset),
  ]).then(([imageResult, printableAsset]) => {
    const { fileSize, height, width, src } = imageResult?.value || {};
    return {
      ...image,
      fileSize,
      fileTimestamp: new Date(image.fileTimestamp),
      height,
      printableAsset: printableAsset.value || null,
      src: src ?? assetsSrc,
      width,
    };
  });
};

const getLetter = (index) =>
  [parseInt(index / 26) - 1, index % 26]
    .map((i) => (i === -1 ? "" : lettersArray[i]))
    .join("");

const getNonImageAssetInfo = (objectID) => async (asset) => {
  if (!asset) return Promise.resolve(null);

  const uri = `/assets/${objectID}/${encodeURIComponent(asset.name)}`;
  return new Promise(async (resolve, reject) => {
    try {
      const { fileSize } = await getXHRFileData(uri);
      const fileTimestamp = asset.fileTimestamp
        ? new Date(asset.fileTimestamp)
        : undefined;
      resolve({
        ...asset,
        fileSize,
        fileTimestamp,
        fileType: getFileType(asset),
        src: uri,
      });
    } catch (err) {
      reject(err);
    }
  });
};

const getMetadataFieldList =
  (metadataConfiguration) => (objectType) => (configurationId) => {
    const fields = metadataConfiguration[objectType]
      .map((field) => {
        if (
          field.values &&
          field.values.length &&
          !field.values[0].configurations.includes(configurationId) &&
          !["multipleSelect", "select"].includes(field.type)
        ) {
          return { label: field.label, values: ["Field not available"] };
        }

        switch (field.type) {
          case "date": {
            return { label: field.label, values: ["Date format YYYY-MM-DD"] };
          }
          case "datetime": {
            // Created Date, First Banked Date, Last Banked Date and Last Modified Date cannot be set through BDU, so
            // exclude them here and drop the nulls via .filter(Boolean) below.
            return null;
          }
          case "integer": {
            return {
              label: field.label,
              values: [`Integer max length: ${field.constraints[0].maxLength}`],
            };
          }
          case "multipleSelect":
          case "select": {
            const values = field.values.length
              ? field.values
                  .filter((value) =>
                    value.configurations.includes(configurationId),
                  )
                  .map((value) => value.stringValue || value.intValue)
              : ["N/A"];
            return { label: field.label, values };
          }
          case "switch": {
            return { label: field.label, values: ["Yes", "No"] };
          }
          case "text": {
            let label = field.label;
            let values = ["A free form text field"];
            if (label === "Item ID") {
              label = "Item Name";
              values = ["A system generated identifier"];
            }
            return { label, values };
          }
          case "user": {
            return {
              label: field.label,
              values: [
                `${field.label}'s name, in the format "Last Name, First Name" `,
              ],
            };
          }
          default: {
            return { label: field.label, values: field.values || [] };
          }
        }
      })
      .filter(Boolean);
    fields.push({
      label: "Standard",
      values: ["A full standard with each colon delimited"],
    });

    return fields.sort((a, b) => a.label.localeCompare(b.label));
  };

const getOptionsByProgram =
  (programs = []) =>
  (selectedProgram = []) =>
  (availableOptions = []) => {
    if (!selectedProgram) return;
    const id = programs.find((p) =>
      selectedProgram.includes(p?.id),
    )?.configurationID;
    const options = availableOptions
      .filter((option) => option?.configurations?.includes(id))
      .map((obj) => ({ label: obj?.stringValue, value: obj?.stringValue }));
    return options;
  };

// The preview engines offered for an item or passage. MJ4 is RIMS with MathJax
// v4 — same endpoint, same generated HTML — so the two travel together.
// Frozen because these are handed straight to components as a shared prop.
const PREVIEW_TYPES = Object.freeze(["MJ4", "RIMS", "TAO", "TN8"]);
const RIMS_PREVIEW_ENGINES = Object.freeze(["MJ4", "RIMS"]);
const EXTERNAL_REVIEW_PREVIEW_TYPES = Object.freeze(
  PREVIEW_TYPES.filter((type) => !RIMS_PREVIEW_ENGINES.includes(type)),
);

// External reviewers are limited to the non-RIMS engines.
const getPreviewTypes = (permissions) =>
  permissions?.includes("externalReview")
    ? EXTERNAL_REVIEW_PREVIEW_TYPES
    : PREVIEW_TYPES;

const getProgramOptions =
  (programs = []) =>
  (permission) =>
    programs
      ?.filter(({ _actions }) => !permission || _actions.includes(permission))
      .map(({ id, name }) => ({ label: name, value: id })) || [];

const getS3SignedUrl =
  (client) =>
  ({ addTimestampToName, fileType, name }) => {
    return client
      .query(
        getS3SignedUrlQuery,
        {
          data: {
            addTimestampToName,
            fileType,
            name,
          },
        },
        { requestPolicy: "network-only" },
      )
      .toPromise()
      .then((response) => {
        if (response.error) throw new Error(response.error);
        return response.data?.getPresignedS3URLs?.[0];
      });
  };

const getS3SignedUrlQuery = `
  query GetPresignedUrl($data: [PresignedS3URLRequest!]) {
    getPresignedS3URLs(data: $data) {
      name, 
      url,
    }
  }`;

const getSelectedItemOptionsFromMetadata =
  (programs) => (ids) => (metadataFieldStates) => {
    return _.mapValues(getOptionsByProgram(programs)(ids))(metadataFieldStates);
  };

const getSelectOptionsFromMetadata =
  (metadataConfiguration) =>
  (programs = []) =>
  (type) =>
  (path) => {
    const programConfigurationIDs = programs.map(
      (program) => program?.configurationID,
    );
    const pathValues = metadataConfiguration[type]
      .find((object) => {
        return object?.path === path;
      })
      ?.values?.filter((value) => {
        return value?.configurations.some((id) =>
          programConfigurationIDs.includes(id),
        );
      });
    return pathValues;
  };

const getTasksByObjects =
  (client) =>
  async ({ id, type }) => {
    return client
      .query(
        getTasksByObjectQuery,
        {
          id,
          type,
        },
        { requestPolicy: "network-only" },
      )
      .toPromise()
      .then((response) => {
        if (response.error) throw new Error(response.error);
        if (!response.data?.tasksByObject) return [];
        return response.data?.tasksByObject.map((task) => {
          return [
            "assignedDateTime",
            "completedDateTime",
            "createdDateTime",
            "dueDate",
          ].reduce(
            (acc, key) => {
              switch (key) {
                case "assignedDateTime":
                case "completedDateTime":
                case "createdDateTime":
                  acc[key] = acc[key] ? new Date(acc[key]) : null;
                  break;
                case "dueDate":
                  acc[key] = acc[key] ? dueDateTransform(acc[key]) : null;
                  break;
                default:
              }
              return acc;
            },
            { ...task },
          );
        });
      });
  };

const getTaskOptions = () =>
  [
    "Create Art",
    "Develop Accessibility",
    "Develop Item",
    "Develop Passage",
    "Develop Stimulus",
    "EDQC",
    "Edit Art",
    "Editorial Review",
    "Final Review Item",
    "Final Review Passage",
    "GAQC",
    "Lead Review",
    "Peer Review Item",
    "Peer Review Passage",
    "Proof Stimulus",
    "Review Item",
    "Review Passage",
    "Review Peer Feedback",
    "Review Stimulus Feedback",
    "Review Stimulus",
    "Revise Art",
    "Rework Item",
    "Rework Passage",
    "Upload Art",
  ].map((value) => ({ label: value, value }));

const getTaskPermissions = (task) =>
  task?.taskDefinition?.execution?.permissions || [];

const getTasksByObjectQuery = `
query GetTasksByObjectQuery($id: ID!, $type: String!) {
    tasksByObject (id: $id, type: $type) {
      _actions
      assignedDateTime
      assignedPools
      assignedUser
      completedByUser
      completedDateTime
      createdByUser
      createdDateTime
      developmentState
      dueDate
      id
      imageDetails {
        component
        description
        file
        format
        type
      }
      instructions
      name
      notes
      program
      qrr
      ready
      requestedDateTime
      snapshotVersionId
      taskDefinition {
        assignment {
          roles
          __typename
          multiple
          pool
          scope
        }
        completion {
          prerequisites
        }
        creation {
          copyTaskDetails
          createOnState
          details
          developmentStates
          names {
            condition
            name
            values
          }
          optional
        }
        execution {
          application
          permissions
        }
        key
      }
    }
  } 
`;

const getUserOptions =
  (users = []) =>
  ({ activeOnly = true, programs = [], roles = [] }) => {
    return filterUsers(users)({ activeOnly, programs, roles }).map((user) => ({
      label: user?.name,
      value: user?.id,
    }));
  };

const getWorkspaceURIFromTask = (task) => {
  const passageID = task.passage?.id || task.item?.passage?.id;
  const queryParams = {
    item: task.item?.id ? `itemID=${task.item.id}` : null,
    passage: passageID ? `passageID=${passageID}` : null,
  };

  return `/workspace?` + Object.values(queryParams).filter(Boolean).join("&");
};

const getXHRFileData = (uri) => {
  if (!uri) return Promise.resolve({});

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("HEAD", uri);
    xhr.responseType = "arraybuffer";
    xhr.onerror = () => {
      reject(`Error loading asset at ${uri}`);
    };
    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.HEADERS_RECEIVED) {
        const fileSize = parseInt(xhr.getResponseHeader("content-length"));
        resolve({
          fileSize: Number.isInteger(fileSize) ? fileSize : undefined,
        });
      }
    };
    xhr.send(null);
  });
};

const isSuperAdmin = (user) => user.superAdmin;

const isProgramAdmin = (user) => (programID) =>
  user.programAccess
    ?.find(({ program }) => program === programID)
    ?.permissions.includes("ProgramAdmin");

const isAdmin = (user) => (programID) =>
  isSuperAdmin(user) || (programID && isProgramAdmin(user)(programID));

const canPerformAction =
  ({ inWorkflow, user }) =>
  (object) =>
  (action) =>
    !!object?._actions?.includes(action) &&
    (!!inWorkflow || (!!user && !!isAdmin(user)(object?.program?.id)));

const lettersArray = [...Array(26)].map((h, i) => String.fromCharCode(65 + i));

const mergeObjects = (obj1) => (obj2) => {
  const result = {};

  // Iterate through keys in obj1
  for (const key in obj1) {
    if (obj1.hasOwnProperty(key)) {
      // If the key exists in obj2, merge the arrays
      if (
        obj2.hasOwnProperty(key) &&
        Array.isArray(obj1[key]) &&
        Array.isArray(obj2[key])
      ) {
        result[key] = obj1[key].concat(obj2[key]);
      } else {
        // If the key doesn't exist in obj2 or is not an array, just use obj1's value
        result[key] = obj1[key];
      }
    }
  }

  // Iterate through keys in obj2
  for (const key in obj2) {
    if (obj2.hasOwnProperty(key) && !obj1.hasOwnProperty(key)) {
      // If the key exists in obj2 but not in obj1, use obj2's value
      result[key] = obj2[key];
    }
  }

  return result;
};

const onDragEnd =
  (move) =>
  ({ destination, source }) => {
    if (!destination) return;
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    )
      return;

    move(source.index, destination.index);
  };

const permissionsInclude =
  ({ activeTasks, type }) =>
  (permission) => {
    const other = ["item", "passage"].find((s) => s !== type);
    const inWorkflow = !!(activeTasks.item || activeTasks.passage);
    // Return true if:
    return (
      // The user is in the workflow, AND
      inWorkflow &&
      // The user's permissions for the given task include the permission, OR
      (getTaskPermissions(activeTasks[type]).includes(permission) ||
        // The user does not have a task for the given type, AND
        // the user's permissions for their other task include the permission
        (!activeTasks[type] &&
          getTaskPermissions(activeTasks[other]).includes(permission)))
    );
  };

const replaceNbspsWithEntityName = (str) => {
  return str.replace(/&nbsp;/g, "&#160;");
};

const setTitle = (title) => {
  const terms = Array.isArray(title) ? title : [title];
  document.title = terms.filter(Boolean).join(" - ");
};

const sortByName = (a, b) => {
  return a.name.localeCompare(b.name, "en", { sensitivity: "base" });
};

const sortByLabel = (a, b) => {
  return a.label.localeCompare(b.label, "en", { sensitivity: "base" });
};

const sortByProgramName = (a, b) => {
  return a.program.name.localeCompare(b.program.name, "en", {
    sensitivity: "base",
  });
};

const transformMathMl = (client) => async (mathml) => {
  return await client
    .query(
      transformMathMlQuery,
      {
        mathML: replaceNbspsWithEntityName(mathml),
      },
      { requestPolicy: "network-only" },
    )
    .toPromise()
    .then((response) => {
      if (response.error) throw new Error(response.error);
      const mathmlToAccessible = JSON.parse(response.data?.mathmlToAccessible);

      return mathmlToAccessible.error
        ? "*** Trouble converting MathML to Text. Please try again ***"
        : mathmlToAccessible.mathText;
    });
};

const transformMathMlQuery = `
  query TransformMathMl($mathML: String!) {
    mathmlToAccessible(mathML: $mathML)
  }
`;

const uniqueObjects = (arr, props = []) => [
  ...new Map(
    arr.map((entry) => [props.map((k) => entry[k]).join("|"), entry]),
  ).values(),
];

const updateTasks = (client) => async (tasks) => {
  const tasksToUpdate = tasks.filter((task) => task._actions.includes("edit"));
  if (!tasksToUpdate) return;
  return client
    .mutation(updateTasksMutation, {
      tasks: tasksToUpdate.map((task) => ({
        id: task.id,
        ready: task.ready,
        qrr: task.qrr,
        notes: task.notes,
      })),
    })
    .toPromise()
    .then((response) => {
      if (response.error) throw new Error(response.error);
      return tasks.map((task, i) => {
        return { ...task, ...response.data?.updateTasks[i] };
      });
    });
};

const updateTasksMutation = `
  mutation UpdateTask($tasks: [UpdateTaskInput!]) {
    updateTasks(tasks: $tasks) {
      id
      notes
      qrr
      ready
    }
  }
`;

const uploadFileToS3 =
  (client) =>
  async ({ addTimestampToName, file }) => {
    const { name: initialName, type: fileType = "image/*" } = file;

    try {
      const { name, url } = await getS3SignedUrl(client)({
        addTimestampToName,
        fileType,
        name: initialName,
      });

      await fetch(url, {
        body: file,
        headers: new Headers({
          "Content-Type": fileType,
        }),
        method: "PUT",
      });

      return { name };
    } catch (err) {
      console.error("File upload failed: %o", err);
    }
  };

const validateItemName = (inputValue) => {
  if (inputValue?.length <= 24 && /^[A-Za-z_]/.test(inputValue)) {
    return null;
  } else {
    return "New Item name must begin with a letter or underscore";
  }
};

const validatePassageName = (inputValue) => {
  if (
    !inputValue ||
    (inputValue?.length <= 50 &&
      /^[A-Za-z0-9_]/.test(inputValue) &&
      !/\s+/.test(inputValue))
  ) {
    return null;
  } else {
    return `New Passage name must begin with a letter, number or underscore, contain no spaces and no more than 50 characters!`;
  }
};

const isPassageNameValid = (name) => !validatePassageName(name);

const stringSortComparator = (v1, v2) => {
  const str1 = v1 ?? "";
  const str2 = v2 ?? "";
  return str1.localeCompare(str2, "en", { sensitivity: "base" });
};

// DataGrid action predicate: disables a row action when the row's `_actions`
// don't include the given action. Curried so it can be used as
// `disabled: disableAction("manageKeywords")`.
const disableAction =
  (action) =>
  ({ _actions }) =>
    !_actions?.includes(action);

export {
  asyncPipe,
  canPerformAction,
  canSave,
  convertSavedTasks,
  convertTasks,
  disableAction,
  displayFileSize,
  dueDateTransform,
  downloadCsv,
  escapeUTF8Characters,
  fetchImageBlob,
  fetchWithAuth,
  filterOutSelectedPrograms,
  filterUsers,
  getAssetFileName,
  getAssetInfo,
  getCompareVersions,
  getDirtyRHFValues,
  getFileType,
  getImageFileData,
  getImageInfo,
  getNonImageAssetInfo,
  getLetter,
  getMetadataFieldList,
  getOptionsByProgram,
  getPreviewTypes,
  getProgramOptions,
  getS3SignedUrl,
  getS3SignedUrlQuery,
  getSelectedItemOptionsFromMetadata,
  getSelectOptionsFromMetadata,
  getTaskOptions,
  getTaskPermissions,
  getTasksByObjects,
  getUserOptions,
  getWorkspaceURIFromTask,
  getXHRFileData,
  isAdmin,
  isPassageNameValid,
  lettersArray,
  mergeObjects,
  onDragEnd,
  permissionsInclude,
  PREVIEW_TYPES,
  RIMS_PREVIEW_ENGINES,
  replaceNbspsWithEntityName,
  setTitle,
  sortByLabel,
  sortByName,
  sortByProgramName,
  sortInactiveTasksByCompletedDate,
  transformMathMl,
  transformMathMlQuery,
  uniqueObjects,
  updateTasks,
  uploadFileToS3,
  validateItemName,
  validatePassageName,
  stringSortComparator,
};
